"use client";

import { useEffect, useState } from "react";
import { capture } from "@/lib/analytics/events";

/**
 * One-question micro-survey, shown ONCE per browser right after a key
 * action — the moment the visitor has just demonstrated intent and is most
 * likely to answer. We barely hear from real users; this is the cheapest
 * always-on channel.
 *
 * Ported verbatim from internationaljobs (CM-140/CM-277), 2026-10-09
 * (T-284) — same component API, same event names, so the two boards stay
 * comparable. financecareers' own FeedbackLink widget only ever appears
 * after a click; this fires on dwell instead, which is the one survey shape
 * this board didn't have.
 *
 * Answers go to PostHog as a `micro_survey_answered` event (no new table, no
 * migration, no backend).
 *
 * The "already asked" flag is a first-party UI preference in localStorage,
 * not analytics storage. It is also the only thing stopping this from
 * becoming a nag: once dismissed or answered, it never appears again on
 * this device.
 */

const SEEN_KEY = "fc.microsurvey.v1";

/**
 * Top-RIGHT, not bottom-right. The cookie banner is a full-width
 * `fixed inset-x-0 bottom-0 z-[60]` bar, and the only visitor who ever sees
 * this survey is a first-time one — who has that banner open. Anchored at
 * the bottom the card would sit under the banner and every click would land
 * there instead. `top-20` clears the sticky header; z-[70] wins over the
 * banner if they ever meet.
 */
const CARD_CLASS =
  "fixed right-4 top-20 z-[70] w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-neutral-200 bg-white p-4 shadow-lg";

/** True the first time this device reaches a key action; false ever after. */
export function shouldAskMicroSurvey(): boolean {
  try {
    if (localStorage.getItem(SEEN_KEY)) return false;
    localStorage.setItem(SEEN_KEY, new Date().toISOString());
    return true;
  } catch {
    // Private mode / storage disabled — never ask rather than ask every time.
    return false;
  }
}

/**
 * Same one-shot-per-device contract as shouldAskMicroSurvey, but keyed
 * per-surveyId instead of sharing a single global flag. Account deletion /
 * subscription cancellation / paywall hesitation are rare, high-signal
 * moments — they should still ask even if this browser already answered an
 * unrelated survey, so they get their own gate.
 */
export function shouldAskExitSurvey(surveyId: string): boolean {
  const key = `fc.exitsurvey.v1.${surveyId}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, new Date().toISOString());
    return true;
  } catch {
    return false;
  }
}

export function MicroSurvey({
  surveyId,
  question,
  placeholder,
  quickOption,
  onClose,
}: {
  /** Stable id for the question, so answers stay comparable as copy changes. */
  surveyId: string;
  question: string;
  placeholder: string;
  /**
   * One-tap answer for yes/no-shaped questions: an open textarea asks for a
   * typed answer even from someone with nothing to report. Optional because
   * most survey call sites ask an open question that has no single-tap
   * answer.
   */
  quickOption?: { label: string; answer: string };
  onClose: () => void;
}) {
  const [answer, setAnswer] = useState("");
  const [sent, setSent] = useState(false);

  // Only a submission event existed before this — impossible to tell "nobody
  // sees it" from "everybody sees it and ignores it." Fires once per mount,
  // i.e. once per device (gated upstream by shouldAskMicroSurvey/shouldAskExitSurvey).
  useEffect(() => {
    capture("micro_survey_shown", { survey_id: surveyId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitAnswer = (text: string, method: "quick_option" | "free_text") => {
    capture("micro_survey_answered", { survey_id: surveyId, answer: text, method });
    setSent(true);
    window.setTimeout(onClose, 1600);
  };

  const doSubmit = () => {
    const text = answer.trim().slice(0, 500);
    if (!text) return;
    submitAnswer(text, "free_text");
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    doSubmit();
  };

  // A plain <textarea> in a <form> does NOT submit on Enter (unlike a
  // single-line <input>), only Shift+Enter-for-newline is the visible
  // affordance here. A quick one-question widget reads like a chat box;
  // people likely type an answer, hit Enter expecting it to send, get a
  // newline instead, and abandon. Enter-to-submit (Shift+Enter for a literal
  // newline) is the standard fix for compact feedback inputs.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      doSubmit();
    }
  };

  return (
    <aside role="dialog" aria-label={question} className={CARD_CLASS}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path
            d="M1 1l10 10M11 1L1 11"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {sent ? (
        <p className="pr-5 text-[14px] font-medium text-ink">
          Thank you — that genuinely helps.
        </p>
      ) : (
        <form onSubmit={submit}>
          <p className="pr-5 text-[14px] font-semibold text-ink">{question}</p>
          {quickOption ? (
            <button
              type="button"
              onClick={() => submitAnswer(quickOption.answer, "quick_option")}
              className="mt-2 inline-flex h-8 items-center rounded-md border border-neutral-300 px-3 text-[13px] font-medium text-ink hover:border-brand-600 hover:text-brand-700"
            >
              {quickOption.label}
            </button>
          ) : null}
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            maxLength={500}
            placeholder={placeholder}
            autoFocus
            className="mt-2 w-full resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 text-[14px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-[13px] text-neutral-500 hover:text-ink"
            >
              Not now
            </button>
            <button
              type="submit"
              disabled={!answer.trim()}
              className="inline-flex h-9 items-center rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-40"
            >
              Send
            </button>
          </div>
        </form>
      )}
    </aside>
  );
}
